import { useReactiveVar } from '@apollo/client'
import { useMemo } from 'react'

import { decodedUserJwtToken } from 'pared/reactiveVariables/user'
import { getBrand } from 'pared/utils/brand'

export interface IChatBoxConfig {
  hasCannedQuestions: boolean
  hasSalesAnalysisButtonForCorp?: boolean
  hasSalesAnalysisButton?: boolean
  hasLaborAnalysisButtonForCorp?: boolean
  hasLaborAnalysisButton?: boolean
  hasCogsAnalysisButtonForCorp?: boolean
  hasCogsAnalysisButton?: boolean
  hasQuestionMenu: boolean
  hasPlv: boolean
  plvDisplayString: string
  hasCogs: boolean
  directorDisplayString: string
  hideDriveThru?: boolean
  hidePmix?: boolean
  hideGuest?: boolean
  hideBuns?: boolean
  hideSos?: boolean
  showChicken?: boolean
}

export interface IExpoAiBrandConfig {
  chatBox: IChatBoxConfig
  showExray: boolean
  showChatBox: boolean
  usePurchasesPage?: boolean
}

export default function useConfig(): IExpoAiBrandConfig {
  const brand = getBrand()
  const jwtToken = useReactiveVar(decodedUserJwtToken)
  const qaAccess = jwtToken.accessible_pages.includes('only-for-qa')
  // Set parameters to
  // 1) qaAccess to enable the buttons only for QA
  // 2) true to enable the buttons to production
  // 3) false to disable the buttons

  return useMemo(() => {
    switch (brand) {
      case 'demo_bk':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasQuestionMenu: true,
            hasPlv: true,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Region',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'supreme_bk':
      case 'supreme_pop':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasQuestionMenu: false,
            hasPlv: true,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Director',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'ace_pop':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: qaAccess,
            hasSalesAnalysisButton: qaAccess,
            hasQuestionMenu: false,
            hasPlv: true,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Director',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'mbn_bk':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasQuestionMenu: false,
            hasPlv: true,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Region',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'irmg_bk':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: qaAccess,
            hasSalesAnalysisButton: qaAccess,
            hasQuestionMenu: false,
            hasPlv: true,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Region',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'timoney_bk':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: true,
            hasSalesAnalysisButton: true,
            hasQuestionMenu: false,
            hasPlv: true,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Region',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'jsc_bk':
      case 'jsc_bk_nso':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasQuestionMenu: false,
            hasPlv: true,
            plvDisplayString: 'AvT',
            hasCogs: false,
            directorDisplayString: 'District Leader',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'ghai_pop':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: true,
            hasSalesAnalysisButton: true,
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: '',
            hasCogs: true,
            directorDisplayString: 'District Leader',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'wingitnorth':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: true,
            hasSalesAnalysisButton: true,
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: 'AvT',
            hasCogs: true,
            directorDisplayString: 'Director',
            hideDriveThru: true,
            hidePmix: true,
            hideGuest: true,
            hideBuns: true,
            showChicken: true,
            hideSos: true,
          },
          showExray: true,
          showChatBox: true,
          usePurchasesPage: true,
        }

      case 'primanti':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'Director',
          },
          showExray: false,
          showChatBox: true,
        }

      case 'mwb':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: true,
            hasLaborAnalysisButtonForCorp: qaAccess,
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: 'AvT',
            hasCogs: true,
            directorDisplayString: 'Director',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'burgerworks':
      case 'dh_wb':
        return {
          chatBox: {
            hasSalesAnalysisButton: qaAccess,
            hasCannedQuestions: false,
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: 'AvT',
            hasCogs: true,
            directorDisplayString: 'Director',
          },
          showExray: true,
          showChatBox: true,
        }

      case 'bibibop':
        return {
          chatBox: {
            hasCannedQuestions: false,
            hasSalesAnalysisButtonForCorp: true, // FIX ME: only for test and should be removed
            hasCogsAnalysisButtonForCorp: qaAccess,
            hasCogsAnalysisButton: qaAccess, // FIX ME: only for QA, should be removed later
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: 'PLV',
            hasCogs: false,
            directorDisplayString: 'District Manager',
          },
          showExray: true,
          showChatBox: true,
        }

      default:
        return {
          chatBox: {
            hasCannedQuestions: true,
            hasQuestionMenu: false,
            hasPlv: false,
            plvDisplayString: 'PLV',
            hasCogs: true,
            directorDisplayString: 'Director',
          },
          showExray: false,
          showChatBox: true,
        }
    }
  }, [brand])
}
